import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DiscoverToken } from '@pleco/shared';
import { useDiscover } from '../lib/discover.js';
import { fmtPrice, fmtUsd } from '../lib/fmt.js';

/** Token logo with a lettered fallback when the image is missing or fails. */
export function TokenLogo({ t, size = 22 }: { t: Pick<DiscoverToken, 'symbol' | 'logoUrl'>; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (t.logoUrl && !broken)
    return (
      <img
        className="tk-logo"
        src={t.logoUrl}
        width={size}
        height={size}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        draggable={false}
      />
    );
  return (
    <span className="tk-logo tk-logo-f" style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }} aria-hidden="true">
      {t.symbol.replace(/^\$/, '').slice(0, 2).toUpperCase()}
    </span>
  );
}

export const Change = ({ v }: { v?: number }) =>
  v === undefined || !Number.isFinite(v) ? null : (
    <span className={`tk-chg ${v >= 0 ? 'up' : 'down'}`}>
      {v >= 0 ? '+' : ''}
      {Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1)}%
    </span>
  );

/** Boosted / new / popular marker. */
export function KindMark({ t }: { t: DiscoverToken }) {
  if (t.kind === 'boosted')
    return (
      <span className="tk-kind boost" title={t.boost ? `Boosted ×${t.boost} on DexScreener` : 'Boosted on DexScreener'}>
        <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="currentColor" d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
        </svg>
        {t.boost ? t.boost : ''}
      </span>
    );
  if (t.kind === 'new' || t.isNew) return <span className="tk-kind new">NEW</span>;
  return null;
}

/**
 * One-line ticker of boosted and freshly launched Solana tokens that scrolls
 * right to left (DexScreener / Axiom style). Hover pauses it; click opens
 * the token's map. New data is swapped in at the end of a loop so the strip
 * never jumps. With reduced motion it becomes a plain scrollable row.
 */
export function TokenTicker({ current }: { current?: string }) {
  const { data, subscribe } = useDiscover();
  useEffect(() => subscribe(), [subscribe]);
  const [shown, setShown] = useState<DiscoverToken[]>([]);
  const pending = useRef<DiscoverToken[]>([]);

  const incoming = (data?.live.length ? data.live : (data?.popular ?? [])).filter((t) => t.address !== current);
  pending.current = incoming;
  useEffect(() => {
    if (!shown.length && incoming.length) setShown(incoming);
  }, [incoming.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shown.length) return <div className="tk-ticker tk-ticker-empty" aria-hidden="true" />;
  // ~3.2 s per token keeps logos readable at any list length.
  const dur = Math.max(40, shown.length * 3.2);
  const item = (t: DiscoverToken, i: number, copy: number) => (
    <Link
      key={`${copy}-${t.address}`}
      to={`/map/sol/${t.address}`}
      className="tk-item"
      title={`${t.name} · ${t.marketCapUsd ? `MC ${fmtUsd(t.marketCapUsd)}` : fmtPrice(t.priceUsd)}`}
      tabIndex={copy ? -1 : 0}
      aria-hidden={copy ? true : undefined}
    >
      <span className="tk-rank">#{i + 1}</span>
      <TokenLogo t={t} size={20} />
      <b>{t.symbol}</b>
      <KindMark t={t} />
      {t.marketCapUsd ? <span className="tk-mc">{fmtUsd(t.marketCapUsd)}</span> : null}
      <Change v={t.change24h} />
    </Link>
  );
  return (
    <nav className="tk-ticker" aria-label="Boosted and new Solana tokens">
      <span className="tk-label">
        <i /> Live
      </span>
      <div className="tk-viewport">
        <div
          className="tk-track"
          style={{ animationDuration: `${dur}s` }}
          onAnimationIteration={() => {
            const next = pending.current;
            if (next.length && next.map((t) => t.address).join() !== shown.map((t) => t.address).join()) setShown(next);
          }}
        >
          {shown.map((t, i) => item(t, i, 0))}
          {shown.map((t, i) => item(t, i, 1))}
        </div>
      </div>
    </nav>
  );
}
